import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, HERO_IMAGE, LOGO_LIGHT, LOGO_DARK, STRIPE_PAYMENT_LINKS } from '../config';

const LandingPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [authMode, setAuthMode] = useState('returning'); // 'new' or 'returning'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); // 'email' or 'code'
  const [loading, setLoading] = useState(false);
  const [giftLoading, setGiftLoading] = useState(false);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/me`);
        // User is logged in - redirect to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err) {
        // Not logged in - stay on landing page
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleGoogleAuth = () => {
    // Redirect to backend's Google OAuth login endpoint
    window.location.href = `${API}/auth/google/login`;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/email/send-code`, { email });
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/email/verify-code`, { email, code });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setStep('email');
    setEmail('');
    setCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowAuthModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">{authMode === 'new' ? '🌱' : '👋'}</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {authMode === 'new' ? 'Start Your Journey' : 'Welcome Back!'}
              </h2>
              <p className="text-gray-600">
                {authMode === 'new' 
                  ? 'Sign in to start your free 14-day trial' 
                  : 'Sign in to continue your journey'}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {step === 'email' ? (
              <>
                {/* Google Auth Button */}
                <button
                  onClick={handleGoogleAuth}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-lg transition duration-200 mb-4"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>

                {/* Email Code Form */}
                <form onSubmit={handleSendCode} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Continue with Email'}
                  </button>
                </form>
              </>
            ) : (
              /* Code Entry Step */
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <p className="text-center text-gray-600 mb-4">
                  We&apos;ve emailed you a 6-digit code at <strong>{email}</strong>. Enter it below.
                </p>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-center text-2xl tracking-widest"
                />
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('email'); setCode(''); setError(''); }}
                  className="w-full text-gray-600 hover:text-gray-900 text-sm"
                >
                  ← Back to email
                </button>
              </form>
            )}

            <button
              onClick={() => setShowAuthModal(false)}
              className="w-full mt-4 text-gray-600 hover:text-gray-900 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src={LOGO_LIGHT} alt="Daily Discipline" className="h-10 w-10" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            <span className="text-lg font-bold text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Daily Discipline™</span>
          </div>
          <a
            href="/blog"
            data-testid="nav-blog-link"
            className="text-white/90 hover:text-white text-sm font-semibold transition"
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
          >
            Blog
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${HERO_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="inline-block bg-emerald-500/20 backdrop-blur-sm text-emerald-100 border border-emerald-300/30 px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-5">
            🌱 Tiny Wins, Daily
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Build discipline,<br/><span className="text-emerald-300">without the guilt.</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 max-w-xl mx-auto leading-relaxed">
            Your AI coach designs one tiny, identity-based habit at a time — and meets you with
            compassion, not shame, on the days you slip.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {/* Button A: Start free for 14 days */}
            <button
              onClick={() => openAuthModal('new')}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base sm:text-lg shadow-xl transition"
            >
              Start Free for 14 Days
            </button>
            {/* Button B: Continue Your Journey */}
            <button
              onClick={() => openAuthModal('returning')}
              className="w-full sm:w-auto bg-white hover:bg-gray-100 text-emerald-600 px-8 py-3.5 rounded-lg font-semibold text-base sm:text-lg shadow-xl transition border-2 border-white"
            >
              Continue Your Journey
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-7 text-white/80 text-xs sm:text-sm font-medium">
            <span>🤖 AI Habit Coach</span>
            <span>💙 No-Guilt Check-ins</span>
            <span>🏋️ Habits + Fitness</span>
            <span>📲 Installable App</span>
          </div>
        </div>
      </section>

      {/* Why It's Different */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-emerald-600 font-semibold text-center mb-3 uppercase tracking-wide text-sm">Not just another habit tracker</p>
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">Why Daily Discipline is different</h2>
          <p className="text-gray-500 text-center max-w-2xl mx-auto mb-14 text-base">
            Most apps hand you an empty checklist and guilt-trip you when you miss a day. We do the opposite.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-7 border border-emerald-100">
              <div className="text-4xl mb-3">🤖</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">An AI coach, not a checklist</h3>
              <p className="text-gray-600 leading-relaxed">
                The <strong>Tiny Wins Architect</strong> interviews you and co-designs one identity-based
                habit so small you can do it on your worst day — then anchors it to a routine you already have.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-7 border border-blue-100">
              <div className="text-4xl mb-3">💙</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No guilt when you slip</h3>
              <p className="text-gray-600 leading-relaxed">
                Miss a couple of days? Instead of a broken-streak shaming screen, you get a kind check-in:
                <em> &ldquo;Life happens.&rdquo;</em> Keep going, make it easier, or pause — your call.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-7 border border-purple-100">
              <div className="text-4xl mb-3">🔄</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Habits that adapt &amp; graduate</h3>
              <p className="text-gray-600 leading-relaxed">
                A mid-point temperature check fine-tunes your pace, and when you finish you choose to
                <strong> level up, maintain, or evolve</strong> into your next journey.
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-7 border border-amber-100">
              <div className="text-4xl mb-3">🏋️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Habits + fitness, in one place</h3>
              <p className="text-gray-600 leading-relaxed">
                Build mindset habits and schedule day-specific workouts (with rest days) side by side —
                with free phone reminders and an installable app.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="text-5xl mb-4">🏗️</div>
              <h3 className="text-xl font-semibold mb-3">1. Meet your Architect</h3>
              <p className="text-gray-600">Chat with your AI coach to design one tiny, identity-based habit anchored to your day.</p>
            </div>
            <div className="text-center">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-xl font-semibold mb-3">2. Show up daily</h3>
              <p className="text-gray-600">One tap to log your win. Gentle phone reminders and streaks keep momentum alive.</p>
            </div>
            <div className="text-center">
              <div className="text-5xl mb-4">🌱</div>
              <h3 className="text-xl font-semibold mb-3">3. Adapt &amp; graduate</h3>
              <p className="text-gray-600">Compassionate check-ins adjust the pace, and you evolve into your next journey.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Based on the Book */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-emerald-400 font-medium mb-3">BASED ON THE BOOK</p>
              <h2 className="text-4xl font-bold mb-6">A Journey to Self-Discipline</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                &ldquo;For years, I struggled to start my podcast. Not because of time or resources—but because of fear, imposter syndrome, and a lack of discipline.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Through deep self-reflection and studying masters like Viktor Frankl, Nelson Mandela, and David Goggins, I discovered: <strong className="text-white">Discipline isn&apos;t a destination. It&apos;s a journey.</strong>&rdquo;
              </p>
              <p className="text-gray-400 mb-8">— Pam Matambanadzo, Author & Founder</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://www.amazon.com/Journey-Self-Discipline-Lessons-inspiration-potential/dp/B0DXPWJD7V"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-3 px-6 rounded-lg transition"
                >
                  📚 Get the Book on Amazon
                </a>
                <button
                  onClick={() => openAuthModal('new')}
                  className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition"
                >
                  📱 Try the App Free
                </button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ca4d87de-0aac-48dd-916c-42dba2a277df/artifacts/98rjze9y_bookcover.jpg"
                  alt="A Journey to Self-Discipline book cover"
                  className="w-64 rounded-lg shadow-2xl transform hover:scale-105 transition duration-300"
                />
                <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                  Available Now
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founding members - honest early-stage social proof (no fabricated quotes) */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-medium px-4 py-1.5 rounded-full mb-5">
            🌱 Just launched
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Be a founding member</h2>
          <p className="text-gray-500 max-w-2xl mx-auto mb-14 text-base">
            You won&apos;t find a wall of testimonials here yet &mdash; we just opened the doors. What you
            <em> will</em> find is a tool built on a clear promise, and the chance to shape it from day one.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100">
              <div className="text-3xl mb-3">💙</div>
              <h3 className="font-semibold text-gray-900 mb-2">No shame, ever</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We&apos;ll never guilt-trip you for missing a day. Slip-ups are part of the journey &mdash; your
                coach meets you with compassion and helps you start again.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100">
              <div className="text-3xl mb-3">🤖</div>
              <h3 className="font-semibold text-gray-900 mb-2">One tiny win at a time</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Your AI coach shrinks an overwhelming goal into a single, identity-based habit you can
                actually keep &mdash; then adapts as you grow.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100">
              <div className="text-3xl mb-3">🗣️</div>
              <h3 className="font-semibold text-gray-900 mb-2">Your voice shapes it</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                As a founding member, your feedback directly guides what we build next. Get in early and
                help create the habit app you wish existed.
              </p>
            </div>
          </div>
          <button
            data-testid="founding-member-cta"
            onClick={() => openAuthModal('new')}
            className="mt-12 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3.5 px-8 rounded-xl transition"
          >
            Become a founding member &rarr;
          </button>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          {/* Quote Bridge */}
          <div className="mb-14 max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-amber-100 via-orange-100 to-yellow-100 rounded-2xl p-10 border-2 border-amber-300 shadow-lg relative">
              <span className="absolute -top-5 left-8 text-7xl text-amber-500 font-serif">&ldquo;</span>
              <p 
                className="text-2xl text-gray-900 leading-relaxed text-center px-8"
                style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontWeight: '500' }}
              >
                Tools, whether physical or digital, act as extensions of your discipline, helping you stay organized, motivated, and on track.
              </p>
              <span className="absolute -bottom-10 right-8 text-7xl text-amber-500 font-serif">&rdquo;</span>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Simple Pricing</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Premium Access */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-10 border-2 border-emerald-200 shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Premium Access</h3>
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-emerald-700">$49/year</span>
                  <span className="bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-medium">Best Value</span>
                </div>
                <p className="text-gray-500">or $7/month</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <span className="text-emerald-600 mr-2">🤖</span>
                  Unlimited AI Habit Coach
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="text-emerald-600 mr-2">🎯</span>
                  Track up to 3 goals at once
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="text-emerald-600 mr-2">🔄</span>
                  Adaptive check-ins &amp; rescue mode
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="text-emerald-600 mr-2">🏋️</span>
                  Fitness scheduler + reminders
                </li>
              </ul>
              <button
                onClick={() => setShowPremiumModal(true)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 rounded-lg transition text-lg"
              >
                Start Your Journey
              </button>
            </div>

            {/* Give a Gift */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-10 border-2 border-purple-200 shadow-lg">
              <div className="text-5xl mb-4">🎁</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Give a Gift</h3>
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-purple-700">$49 (1 Year)</span>
                  <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">Best Value</span>
                </div>
                <p className="text-gray-500">or $19 (90 Days)</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <span className="text-purple-600 mr-2">✓</span>
                  The perfect tool for a friend
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="text-purple-600 mr-2">✓</span>
                  Non-renewing (one-time)
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="text-purple-600 mr-2">✓</span>
                  Instant Delivery
                </li>
              </ul>
              <button
                onClick={() => setShowGiftModal(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 rounded-lg transition text-lg"
              >
                Send as a Gift
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Modal */}
      {showGiftModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowGiftModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎁</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Gift Daily Discipline™</h2>
              <p className="text-gray-600">Give someone the gift of building better habits</p>
            </div>

            {/* Gift Options */}
            <div className="space-y-4 mb-6">
              {/* 90-Day Jumpstart Option */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-4 hover:border-emerald-400 transition cursor-pointer"
                   onClick={() => {
                     setGiftLoading(true);
                     window.location.href = STRIPE_PAYMENT_LINKS.GIFT_90DAY;
                   }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">90-Day Jumpstart</div>
                    <div className="text-sm text-gray-600">Perfect for trying it out</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-600">$19</div>
                    <div className="text-xs text-gray-500">3 months</div>
                  </div>
                </div>
              </div>

              {/* 1 Year Option */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 hover:border-purple-400 transition cursor-pointer relative"
                   onClick={() => {
                     setGiftLoading(true);
                     window.location.href = STRIPE_PAYMENT_LINKS.GIFT_YEAR;
                   }}>
                <div className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Best Value
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">1 Year of Growth</div>
                    <div className="text-sm text-gray-600">Full year of discipline</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">$49</div>
                    <div className="text-xs text-gray-500">12 months</div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-sm text-center mb-4">
              You&apos;ll enter the recipient&apos;s email on the next page
            </p>

            {giftLoading && (
              <div className="text-center py-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            )}

            <button
              onClick={() => setShowGiftModal(false)}
              className="w-full mt-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowPremiumModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🚀</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
              <p className="text-gray-600">Start building better habits today</p>
            </div>

            {/* Premium Options */}
            <div className="space-y-4 mb-6">
              {/* Monthly Option */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-gray-200 rounded-xl p-4 hover:border-gray-400 transition cursor-pointer"
                   onClick={() => {
                     setPremiumLoading(true);
                     window.location.href = STRIPE_PAYMENT_LINKS.MONTHLY;
                   }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">Monthly</div>
                    <div className="text-sm text-gray-600">Flexible, cancel anytime</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-700">$7</div>
                    <div className="text-xs text-gray-500">per month</div>
                  </div>
                </div>
              </div>

              {/* Annual Option */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-4 hover:border-emerald-400 transition cursor-pointer relative"
                   onClick={() => {
                     setPremiumLoading(true);
                     window.location.href = STRIPE_PAYMENT_LINKS.ANNUAL;
                   }}>
                <div className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Best Value
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">Annual</div>
                    <div className="text-sm text-gray-600">Save $35/year</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-600">$49</div>
                    <div className="text-xs text-gray-500">per year</div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-sm text-center mb-4">
              14-day free trial included with all plans
            </p>

            {premiumLoading && (
              <div className="text-center py-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            )}

            <button
              onClick={() => setShowPremiumModal(false)}
              className="w-full mt-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <img src={LOGO_LIGHT} alt="Daily Discipline" className="h-12 w-12 mx-auto mb-4 opacity-60" />
          <nav className="flex items-center justify-center gap-6 mb-4 text-sm">
            <a href="/blog" className="hover:text-white transition" data-testid="footer-blog-link">Blog</a>
            <a href="/legal/terms" className="hover:text-white transition">Terms</a>
            <a href="/legal/privacy" className="hover:text-white transition">Privacy</a>
          </nav>
          <p className="text-sm">© 2026 Daily Discipline™. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
